import { useState } from "react";
import { ChevronRight, CircleUserRound, ReceiptText } from "lucide-react";
import { ApiError, type UpdateMeRequest } from "@ferrivia/api-contract";
import { useApi, useSession, useTracker } from "@ferrivia/auth-web";
import { localeSchema, setLocale, useMarket, useTranslation, type Locale } from "@ferrivia/i18n";
import {
  BuildVersion,
  Button,
  Field,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
  Text,
} from "@ferrivia/ui";
import { useAppContext } from "../../app/useAppContext";
import { useDismissOnBack } from "../../hooks/useDismissOnBack";
import { OrdersSheet } from "../IronPanelPurchase";
import s from "./ProfileMenu.module.css";

// Language names in their own language, not translated — "Suomi" doesn't
// change depending on which language is currently active, unlike every
// other string in this file. Still routed through the catalog rather than
// hardcoded here, so it stays visible to the same i18n tooling as everything
// else (i18next-parser.config.js).
const LANGUAGE_OPTIONS = localeSchema.options;

type ContactUpdate = Pick<UpdateMeRequest, "preferredName" | "email" | "phone">;

// Matches profile.details_updated's `fields` enum in the analytics catalog —
// see packages/analytics/src/catalog.ts. Mirrors clinic's DetailsPane, the
// staff-editing-a-customer analogue of this same self-editing form.
const CONTACT_FIELD_NAMES: Record<keyof ContactUpdate, "preferred_name" | "email" | "phone"> = {
  preferredName: "preferred_name",
  email: "email",
  phone: "phone",
};

/**
 * Editing your own contact details, and the one way out of the app.
 *
 * A sheet rather than a dropdown anchored under the icon: the trigger sits in
 * the top-right corner, which is the far end of a phone's thumb reach, and a
 * menu opening from there puts its actions further away still. A sheet brings
 * them down to the bottom of the screen instead, reuses the one overlay
 * primitive this app already handles safe areas, the keyboard and
 * drag-to-dismiss for, and grows into the prototype's fuller profile list
 * (plan, notifications, language) without becoming a different component.
 * No dedicated close button: the sheet's own drag-to-dismiss and the back
 * gesture (`useDismissOnBack`, below) are the close mechanism already.
 *
 * Deliberately NOT shared with apps/clinic's AccountSheet, which solves the
 * same problem for the console's mobile breakpoint and which this mirrors:
 * the overlapping part is the title/sign-out/BuildVersion body, and
 * everything around it differs (Dialog vs Sheet, staff identity vs none,
 * an externally controlled open vs its own trigger, clinic.signed_out vs
 * profile.*). Promoting that body to packages/ui was weighed and declined
 * (Ville, 2026-09-05): it is ~40 lines, and the two are expected to diverge
 * rather than converge — this one toward the client's profile list, that one
 * toward staff identity. Revisit if they start moving together instead.
 *
 * Uncontrolled from the caller's side — nothing outside needs to open it —
 * but controlled internally, which is what `useDismissOnBack` and the sheet's
 * own drag-to-dismiss both require.
 *
 * The contact fields (preferred name, email, phone) are the client editing
 * their own `users` row via `PATCH /me` — self-service, so unlike the
 * clinic's DetailsPane (staff correcting a *customer's* details) this goes
 * through no `AccessContext`/`auditedWrite`: a person reading or writing
 * their own data is not a disclosure to a third party, which is what
 * asiakastietolaki 10 § and the access log exist to track. A save calls
 * `session.refresh()` so the sheet (and anything else reading the signed-in
 * user) picks up the new values from the one shared session state rather
 * than carrying its own copy.
 *
 * The language switcher writes both sides deliberately: `setLocale`
 * (`@ferrivia/i18n`) flips the running instance and persists a localStorage
 * override immediately — the same override `resolveLocale`'s bootstrap chain
 * checks before ever looking at `user.locale`, so the choice sticks on this
 * device even if the server write below fails — and `PATCH /me` backfills
 * `user.locale` so a fresh sign-in on another device starts from the same
 * choice. `useLocaleSync` already documents the other half of this contract
 * (it backfills `user.locale` once, the first time it's null); this is the
 * explicit-choice path that comment calls "a future switcher UI".
 */
export interface ProfileMenuProps {
  /** Merged onto the trigger button. How the trigger aligns against whatever
      it shares a row with belongs to that row, not to this component — see
      `.profile` in HomeScreen.module.css. */
  className?: string;
  /** Distinguishes which instance a test is driving now that NavRail renders
      a second one alongside HomeScreen's — both share the same accessible
      name (`profile.open`), so a role/name query alone can't tell them apart. */
  "data-testid"?: string;
}

export function ProfileMenu({ className, "data-testid": testId }: ProfileMenuProps) {
  const { t, i18n } = useTranslation("client");
  const commerce = useMarket() === "fi";
  const { rootElement, signOut, user } = useAppContext();
  const api = useApi();
  const session = useSession();
  const tracker = useTracker();
  const [open, setOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  // Back closes the menu instead of leaving the app — same reasoning as the
  // symptom check-in sheet's, see the hook.
  useDismissOnBack(open, () => setOpen(false), "profileMenu");

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) tracker.track("profile.menu_opened", {});
  }

  // Stacked on top of this sheet rather than replacing it: closing one sheet
  // and opening another in the same tick would race their history entries
  // (useDismissOnBack), and back from the orders lands here, where it came
  // from.
  function handleOpenOrders() {
    tracker.track("store.orders_opened", { source: "profile_menu" });
    setOrdersOpen(true);
  }

  function handleSignOut() {
    tracker.track("profile.signed_out", {});
    setOpen(false);
    signOut();
  }

  const baselinePreferredName = user.preferredName ?? "";
  const baselineEmail = user.email ?? "";
  const baselinePhone = user.phone ?? "";
  const [preferredName, setPreferredName] = useState(baselinePreferredName);
  const [email, setEmail] = useState(baselineEmail);
  const [phone, setPhone] = useState(baselinePhone);
  const [savingContact, setSavingContact] = useState(false);
  const [contactSaveFailed, setContactSaveFailed] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const contactDirty =
    preferredName !== baselinePreferredName || email !== baselineEmail || phone !== baselinePhone;

  async function handleContactSave() {
    const update: ContactUpdate = {};
    if (preferredName !== baselinePreferredName) {
      update.preferredName = preferredName.trim().length > 0 ? preferredName.trim() : null;
    }
    if (email !== baselineEmail) {
      update.email = email.trim().length > 0 ? email.trim() : null;
    }
    if (phone !== baselinePhone) {
      update.phone = phone.trim().length > 0 ? phone.trim() : null;
    }
    if (Object.keys(update).length === 0) {
      return;
    }

    setSavingContact(true);
    setContactSaveFailed(false);
    setEmailError(null);
    try {
      await api.updateMe(update);
      await session.refresh();
      tracker.track("profile.details_updated", {
        fields: (Object.keys(update) as (keyof ContactUpdate)[]).map((key) => CONTACT_FIELD_NAMES[key]),
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === "email_in_use") {
        setEmailError(t("profile.contact.emailInUse", "Tämä sähköposti on jo toisen tilin käytössä."));
      } else {
        setContactSaveFailed(true);
      }
      tracker.track("profile.details_update_failed", {
        reason: err instanceof ApiError ? err.code : "network",
      });
    } finally {
      setSavingContact(false);
    }
  }

  async function handleLanguageChange(value: string) {
    // Safe to parse rather than cast: every SelectItem value below is drawn
    // from LANGUAGE_OPTIONS, so this can only fail on a stale build's cached JS.
    const locale: Locale = localeSchema.parse(value);
    setLocale(i18n, locale);
    tracker.track("profile.language_changed", { locale });
    try {
      await api.updateMe({ locale });
      await session.refresh();
    } catch {
      // The local switch above already took effect and is the source of
      // truth going forward — resolveLocale's stored-override step outranks
      // user.locale from here on — so a failed backend sync isn't user-visible
      // and isn't worth retrying or reporting.
    }
  }

  const languageLabels: Record<Locale, string> = {
    fi: t("profile.language.fi", "Suomi"),
    en: t("profile.language.en", "English"),
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <button
          type="button"
          className={className ? `${s.trigger} ${className}` : s.trigger}
          aria-label={t("profile.open", "Oma profiili")}
          data-testid={testId}
        >
          {/* Decorative: the button's aria-label is what gets announced. */}
          <CircleUserRound size={26} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </SheetTrigger>
      <SheetContent
        container={rootElement}
        aria-describedby={undefined}
        // Radix's default: focus the first focusable descendant on open,
        // which is now the preferred-name field — that would pop the
        // keyboard open on a phone the instant the sheet appears, before
        // anyone's asked to edit anything.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetTitle className={s.title}>{t("profile.title", "Oma profiili")}</SheetTitle>

        {commerce && (
          <button type="button" className={s.ordersRow} onClick={handleOpenOrders}>
            <ReceiptText size={20} strokeWidth={1.75} aria-hidden="true" />
            <span className={s.ordersLabel}>{t("profile.orders", "Tilaukseni")}</span>
            <ChevronRight size={18} strokeWidth={1.75} aria-hidden="true" />
          </button>
        )}

        <div className={s.contact}>
          <Field
            label={t("profile.contact.preferredName", "Kutsumanimi")}
            autoComplete="nickname"
            value={preferredName}
            onChange={(e) => setPreferredName(e.target.value)}
            disabled={savingContact}
          />
          <Field
            label={t("profile.contact.email", "Sähköposti")}
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={savingContact}
            errorText={emailError ?? undefined}
          />
          <Field
            label={t("profile.contact.phone", "Puhelinnumero")}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={savingContact}
          />
          {contactSaveFailed && (
            <Text as="p" size="sm" className={s.error}>
              {t("profile.contact.saveFailed", "Tallennus epäonnistui. Yritä uudelleen.")}
            </Text>
          )}
        </div>

        <div className={s.language}>
          <Text as="label" size="sm" weight="medium" id="language-label">
            {t("profile.language.label", "Kieli")}
          </Text>
          <Select value={i18n.language} onValueChange={handleLanguageChange}>
            <SelectTrigger aria-labelledby="language-label" />
            <SelectContent>
              {LANGUAGE_OPTIONS.map((locale) => (
                <SelectItem key={locale} value={locale}>
                  {languageLabels[locale]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Save and sign-out are the sheet's only two actions, so they sit
            together at the end regardless of which fields above they act
            on — Save doesn't touch the language switcher between them. */}
        <div className={s.actions}>
          <Button
            variant="primary"
            className={s.action}
            onClick={handleContactSave}
            disabled={!contactDirty || savingContact}
          >
            {t("profile.contact.save", "Tallenna")}
          </Button>
          <Button variant="secondary" className={s.action} onClick={handleSignOut}>
            {t("profile.signOut", "Kirjaudu ulos")}
          </Button>
        </div>

        <BuildVersion
          env={import.meta.env.VITE_APP_ENV}
          version={import.meta.env.VITE_APP_VERSION}
          className={s.buildVersion}
        />

        {commerce && <OrdersSheet open={ordersOpen} onOpenChange={setOrdersOpen} />}
      </SheetContent>
    </Sheet>
  );
}
